import type { ReportConfig, ReportData } from "../types.js";

/**
 * Generate a clean, professional markdown report.
 */
export function generateMarkdownReport(
  data: ReportData,
  config: ReportConfig,
): string {
  const lines: string[] = [];

  lines.push(`# Project Report: ${data.project.name}`);
  lines.push("");
  lines.push(`**Report Date:** ${data.project.reportDate}`);
  if (config.dateRange) {
    lines.push(
      `**Period:** ${data.project.startDate} \u2013 ${data.project.reportDate}`,
    );
  }
  lines.push("");

  // Summary
  lines.push("## Summary");
  lines.push("");
  lines.push("| Metric | Value |");
  lines.push("|--------|-------|");

  const completePct =
    data.summary.featuresTotal > 0
      ? Math.round(
          (data.summary.featuresComplete / data.summary.featuresTotal) * 100,
        )
      : 0;
  lines.push(
    `| Features | ${data.summary.featuresComplete} of ${data.summary.featuresTotal} complete (${completePct}%) |`,
  );
  lines.push(`| Total Cost | $${data.summary.totalCost.toFixed(2)} |`);

  if (data.summary.budgetUsed > 0) {
    const budgetTotal =
      data.summary.budgetUsed > 0
        ? (data.summary.totalCost / data.summary.budgetUsed) * 100
        : 0;
    lines.push(
      `| Budget Used | ${data.summary.budgetUsed}% of $${budgetTotal.toFixed(2)} |`,
    );
  }

  if (data.summary.estimateAccuracy > 0) {
    lines.push(`| Estimate Accuracy | ${data.summary.estimateAccuracy}% |`);
  }

  lines.push(
    `| Total Tokens | ${data.summary.totalTokens.toLocaleString()} |`,
  );

  if (data.summary.featuresInProgress > 0) {
    lines.push(`| In Progress | ${data.summary.featuresInProgress} features |`);
  }

  lines.push("");

  // Feature Progress
  if (config.includeFeatureDetails && data.features.length > 0) {
    lines.push("## Feature Progress");
    lines.push("");
    lines.push(
      "| Feature | Status | Est. Cost | Actual Cost | Sessions | Days |",
    );
    lines.push(
      "|---------|--------|-----------|-------------|----------|------|",
    );

    for (const f of data.features) {
      const statusLabel = formatStatus(f.status);
      lines.push(
        `| ${f.name} | ${statusLabel} | $${f.estimatedCost.toFixed(2)} | $${f.actualCost.toFixed(2)} | ${f.sessions} | ${f.daysElapsed.toFixed(1)} |`,
      );
    }

    lines.push("");
  }

  // Cost Breakdown
  if (config.includeCostBreakdown) {
    lines.push("## Cost Breakdown");
    lines.push("");

    if (data.costBreakdown.byModel.length > 0) {
      lines.push("### By Model");
      lines.push("| Model | Tokens | Cost | % of Total |");
      lines.push("|-------|--------|------|-----------|");

      for (const m of data.costBreakdown.byModel) {
        lines.push(
          `| ${m.model} | ${formatTokens(m.tokens)} | $${m.cost.toFixed(2)} | ${m.percentage}% |`,
        );
      }

      lines.push("");
    }

    if (data.costBreakdown.byFeature.length > 0) {
      lines.push("### By Feature");
      lines.push("| Feature | Cost | % of Total |");
      lines.push("|---------|------|-----------|");

      for (const f of data.costBreakdown.byFeature) {
        lines.push(`| ${f.feature} | $${f.cost.toFixed(2)} | ${f.percentage}% |`);
      }

      lines.push("");
    }
  }

  // Velocity
  if (config.includeVelocityMetrics) {
    lines.push("## Velocity");
    lines.push("");
    const trendIndicator =
      data.velocity.trend === "improving"
        ? "improving \u2191"
        : data.velocity.trend === "declining"
          ? "declining \u2193"
          : "stable \u2192";
    lines.push(
      `- **Throughput:** ${data.velocity.featuresPerWeek} features/week (${trendIndicator})`,
    );
    lines.push(
      `- **Avg Cost/Feature:** $${data.velocity.avgCostPerFeature.toFixed(2)}`,
    );
    lines.push(
      `- **Avg Time/Feature:** ${data.velocity.avgDaysPerFeature} days`,
    );
    lines.push("");
  }

  // Estimate Accuracy
  if (
    config.includeEstimateAccuracy &&
    data.estimateAccuracy.byComplexity.length > 0
  ) {
    lines.push("## Estimate Accuracy");
    lines.push("");
    lines.push("| Complexity | Accuracy | Data Points |");
    lines.push("|-----------|----------|-------------|");

    for (const c of data.estimateAccuracy.byComplexity) {
      lines.push(`| ${c.complexity} | ${c.accuracy}% | ${c.dataPoints} |`);
    }

    lines.push("");
  }

  // Footer
  lines.push("---");
  lines.push("*Generated by Fathom \u2014 AI Workflow Intelligence*");
  lines.push("");

  return lines.join("\n");
}

// ── Helpers ──────────────────────────────────────────────────

function formatStatus(status: string): string {
  switch (status) {
    case "complete":
      return "\u2713 Complete";
    case "in_progress":
      return "In Progress";
    case "pending":
      return "Pending";
    default:
      return status;
  }
}

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(1)}M`;
  }
  if (tokens >= 1_000) {
    return `${(tokens / 1_000).toFixed(0)}K`;
  }
  return tokens.toString();
}
